import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight, DollarSign, Users, Clock, MapPin } from 'lucide-react';

const OUTCOMES = [
  {
    Icon: DollarSign,
    color: 'orange',
    stat: '↓ 15–30%',
    title: 'Identify high-cost employees',
    desc: 'See which employees cost significantly more than your team average and take action before the next pay period.',
  },
  {
    Icon: Users,
    color: 'red',
    stat: 'Every shift',
    title: 'Detect overstaffed days',
    desc: 'The Labor Dashboard flags days where cost exceeded your daily average by 20%+ so you can schedule smarter next time.',
  },
  {
    Icon: Clock,
    color: 'amber',
    stat: 'Automatically',
    title: 'Reduce overtime accumulation',
    desc: "Eva Insights tracks overtime per employee and alerts you early — not after payroll closes when it's too late.",
  },
  {
    Icon: MapPin,
    color: 'teal',
    stat: 'Any time',
    title: 'Compare across locations',
    desc: 'Find out which location costs 15%+ more per hour and why — without running a single manual report.',
  },
];

const COLOR_MAP = {
  orange: { stat: 'text-orange-600', bg: 'bg-orange-50',  border: 'border-orange-100', icon: 'text-orange-500', iconBg: 'bg-orange-100' },
  red:    { stat: 'text-red-600',    bg: 'bg-red-50',     border: 'border-red-100',    icon: 'text-red-500',    iconBg: 'bg-red-100'    },
  amber:  { stat: 'text-amber-600',  bg: 'bg-amber-50',   border: 'border-amber-100',  icon: 'text-amber-500',  iconBg: 'bg-amber-100'  },
  teal:   { stat: 'text-teal-600',   bg: 'bg-teal-50',    border: 'border-teal-100',   icon: 'text-teal-500',   iconBg: 'bg-teal-100'   },
};

export function OutcomesSection() {
  return (
    <section
      className="py-16 md:py-24 bg-slate-50 px-4"
      aria-label="What you can do"
      id="outcomes"
    >
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-12 md:mb-16">
          <p className="text-orange-500 text-xs font-bold uppercase tracking-widest mb-3">Concrete outcomes</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight"
              style={{ fontFamily: 'Manrope' }}
            >
              What you can actually do
              <br className="hidden sm:block" /> with ReStaff.
            </h2>
            <Link to="/register" className="shrink-0">
              <Button
                variant="outline"
                className="border-orange-300 text-orange-600 hover:bg-orange-50 whitespace-nowrap"
                data-testid="outcomes-cta-btn"
              >
                Try it free
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Outcome cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {OUTCOMES.map(({ Icon, color, stat, title, desc }) => {
            const cls = COLOR_MAP[color];
            return (
              <div
                key={title}
                className={`rounded-2xl border p-7 ${cls.bg} ${cls.border}`}
                data-testid={`outcome-card-${color}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl ${cls.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={18} className={cls.icon} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-black uppercase tracking-widest mb-1 ${cls.stat}`}>{stat}</p>
                    <h3 className="text-slate-900 font-bold text-lg mb-2" style={{ fontFamily: 'Manrope' }}>
                      {title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
